import {
  LogicalOperator,
  SearchOperator,
  type SearchCriteria,
  type SearchQueryRequest,
} from "@/openapi/generated";

export interface ClassFiltersState {
  className: string;
  active: boolean | null;
  seasonId: number | null;
  locationId: number | null;
  teacherId: number | null;
  /** DayOfWeek values, e.g. "Monday". Empty = no day filter. */
  days: string[];
  /** null = all, true = recital only, false = non-recital only */
  recital: boolean | null;
}

export const EMPTY_CLASS_FILTERS: ClassFiltersState = {
  className: "",
  active: null,
  seasonId: null,
  locationId: null,
  teacherId: null,
  days: [],
  recital: null,
};

function daysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((day, index) => day === sortedB[index]);
}

export function classFiltersEqual(
  a: ClassFiltersState,
  b: ClassFiltersState,
): boolean {
  return (
    a.className === b.className &&
    a.active === b.active &&
    a.seasonId === b.seasonId &&
    a.locationId === b.locationId &&
    a.teacherId === b.teacherId &&
    a.recital === b.recital &&
    daysEqual(a.days, b.days)
  );
}

export function countClassFilters(filters: ClassFiltersState): number {
  let count = 0;
  if (filters.className.trim()) count++;
  if (filters.active !== null) count++;
  if (filters.seasonId !== null) count++;
  if (filters.locationId !== null) count++;
  if (filters.teacherId !== null) count++;
  if (filters.days.length > 0) count++;
  if (filters.recital !== null) count++;
  return count;
}

/** Modal badge count - excludes class name when quick search owns it. */
export function countClassModalFilters(
  filters: ClassFiltersState,
  quickClassName = "",
): number {
  let count = countClassFilters(filters);
  if (quickClassName.trim() && filters.className.trim()) {
    count -= 1;
  }
  return count;
}

export function countActiveClassCriteria(
  quickClassName: string,
  filters: ClassFiltersState,
): number {
  let count = countClassFilters(filters);
  if (quickClassName.trim()) {
    if (!filters.className.trim()) count += 1;
  }
  return count;
}

export function hasActiveClassFilters(filters: ClassFiltersState): boolean {
  return countClassFilters(filters) > 0;
}

export function hasActiveClassSearch(
  quickClassName: string,
  filters: ClassFiltersState,
): boolean {
  return quickClassName.trim() !== "" || hasActiveClassFilters(filters);
}

const DAY_ABBREVIATIONS: Record<string, string> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

export function formatClassDaysFilterLabel(days: string[]): string {
  if (days.length === 0) return "";
  return days
    .map((day) => DAY_ABBREVIATIONS[day] ?? day)
    .join(", ");
}

function appendCriterion(
  criteria: SearchCriteria[],
  field: string,
  operator: SearchOperator,
  value: string | number | boolean | string[] | number[],
): void {
  criteria.push({
    field,
    operator,
    value,
    logicalOperator:
      criteria.length > 0 ? LogicalOperator.And : undefined,
  });
}

export function buildClassSearchQuery(
  quickClassName: string,
  filters: ClassFiltersState,
): SearchQueryRequest | null {
  const criteria: SearchCriteria[] = [];

  const className = quickClassName.trim() || filters.className.trim();
  if (className) {
    appendCriterion(criteria, "Name", SearchOperator.Contains, className);
  }

  if (filters.seasonId !== null) {
    if (filters.seasonId === 0) {
      appendCriterion(criteria, "IsContinuous", SearchOperator.Equals, true);
    } else {
      appendCriterion(criteria, "Season.Id", SearchOperator.Equals, filters.seasonId);
    }
  }

  if (filters.locationId !== null) {
    appendCriterion(
      criteria,
      "Location.Id",
      SearchOperator.Equals,
      filters.locationId,
    );
  }

  if (filters.teacherId !== null) {
    appendCriterion(
      criteria,
      "Teachers.UserId",
      SearchOperator.Equals,
      filters.teacherId,
    );
  }

  if (filters.recital !== null) {
    appendCriterion(criteria, "Recital", SearchOperator.Equals, filters.recital);
  }

  if (filters.days.length === 1) {
    appendCriterion(
      criteria,
      "ClassDays.Day",
      SearchOperator.Equals,
      filters.days[0],
    );
  } else if (filters.days.length > 1) {
    appendCriterion(
      criteria,
      "ClassDays.Day",
      SearchOperator.In,
      filters.days,
    );
  }

  if (criteria.length === 0) return null;
  return { criteria };
}

/**
 * Body for POST /classes/search. When undefined, BE returns all classes
 * (same as scroll) - only pass criteria when filters or name search are active.
 */
export function toClassSearchBody(
  searchQueryRequest: SearchQueryRequest | null | undefined,
): SearchQueryRequest | undefined {
  const criteria = searchQueryRequest?.criteria;
  if (criteria && criteria.length > 0) {
    return searchQueryRequest;
  }
  return undefined;
}

export type ClassFilterChipKey =
  | keyof ClassFiltersState
  | "quickClassName";

export function formatClassesListDescription(
  totalCount: number,
  quickClassName: string,
  filters: ClassFiltersState,
): string {
  const matchWord = totalCount === 1 ? "class" : "classes";
  const criteriaCount = countActiveClassCriteria(quickClassName, filters);
  const quickQuery = quickClassName.trim();

  if (criteriaCount === 0) {
    return `${totalCount} ${matchWord}`;
  }

  if (quickQuery && criteriaCount === 1) {
    return `${totalCount} ${matchWord} with name “${quickQuery}”`;
  }

  if (quickQuery) {
    return `${totalCount} ${matchWord} matching “${quickQuery}” and ${criteriaCount - 1} other ${
      criteriaCount - 1 === 1 ? "criterion" : "criteria"
    }`;
  }

  return `${totalCount} ${matchWord} matching ${criteriaCount} search ${
    criteriaCount === 1 ? "criterion" : "criteria"
  }`;
}
